import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, CheckCircle2, Eye, EyeOff, KeyRound, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SeoHead, SEO_PRESETS } from "@/components/SeoHead";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PW_RULE = "Password must be at least 8 characters and include uppercase, lowercase, and a number.";

function validatePassword(pw) {
  if (pw.length < 8) return PW_RULE;
  if (!/[A-Z]/.test(pw)) return PW_RULE;
  if (!/[a-z]/.test(pw)) return PW_RULE;
  if (!/\d/.test(pw)) return PW_RULE;
  return null;
}

/** Direct create-password flow.
 *
 * Students enter their approved email + new password + confirmation. The
 * backend verifies the email against the Student Access list (uploaded by
 * admin via Excel/CSV) and creates the password immediately. No setup token /
 * email link required.
 */
export default function CreatePassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Please enter your registered email address.");
      return;
    }
    const pwErr = validatePassword(pw);
    if (pwErr) { setError(pwErr); return; }
    if (pw !== confirm) { setError("Passwords do not match."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/create-password", {
        email: email.trim().toLowerCase(),
        password: pw,
        confirm_password: confirm,
      });
      setDone(true);
      toast.success("Your password has been created successfully. Please sign in.");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-background overflow-x-hidden">
      <SeoHead {...SEO_PRESETS.createPassword} />
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="glow-orb glow-orb-teal w-[500px] h-[500px] -top-32 -right-32" />
      </div>
      <header className="relative z-10">
        <div className="container flex h-16 sm:h-20 items-center justify-between">
          <Logo size="md" />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button asChild variant="ghost" data-testid="create-pw-back-btn">
              <Link to="/login"><ArrowLeft className="h-4 w-4" /> Sign In</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 px-4 py-12 flex items-center justify-center">
        <Card className="glass-card w-full max-w-md">
          <CardContent className="p-7 sm:p-10">
            {done ? (
              <div className="text-center" data-testid="create-pw-success">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-teal/15 flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-7 w-7 text-teal" />
                </div>
                <h1 className="text-xl font-heading font-bold">Password Created</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  Your password has been created successfully. Redirecting to the sign-in page...
                </p>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="mx-auto h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-3">
                    <KeyRound className="h-5 w-5 text-primary" />
                  </div>
                  <h1 className="text-2xl font-heading font-bold tracking-tight">Create Your Password</h1>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Portal eLearningSaham by Maahad Bursa</p>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    Enter your registered email address to create your student portal password.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="create-pw-form">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="create-pw-email"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pw">New Password</Label>
                    <div className="relative">
                      <Input
                        id="pw"
                        type={show ? "text" : "password"}
                        autoComplete="new-password"
                        value={pw}
                        onChange={(e) => setPw(e.target.value)}
                        required
                        data-testid="create-pw-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShow((s) => !s)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label="Toggle password visibility"
                      >
                        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm">Confirm Password</Label>
                    <Input
                      id="confirm"
                      type={show ? "text" : "password"}
                      autoComplete="new-password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      data-testid="create-pw-confirm"
                    />
                  </div>

                  <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground leading-relaxed">
                    <ShieldCheck className="h-3.5 w-3.5 text-teal shrink-0 mt-0.5" />
                    Min 8 characters · 1 uppercase · 1 lowercase · 1 number
                  </p>

                  {error && (
                    <p className="text-sm text-destructive" data-testid="create-pw-error">{error}</p>
                  )}

                  <Button
                    type="submit"
                    className="w-full btn-glow"
                    size="lg"
                    disabled={submitting}
                    data-testid="create-pw-submit"
                  >
                    {submitting ? "Creating..." : "Create Password"}
                  </Button>

                  <p className="text-sm text-center text-muted-foreground pt-2">
                    Already have a password?{" "}
                    <Link to="/login" className="text-primary hover:underline font-medium" data-testid="create-pw-signin-link">
                      Sign in here
                    </Link>
                  </p>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
